import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMail, FiMapPin, FiPhone, FiSend, FiCheck } = FiIcons;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
    }, 1000);
  };

  return (
    <div className="pt-24 pb-16">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center space-y-8"
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-5xl md:text-6xl font-bold leading-tight"
            >
              Contact <span className="gradient-text">Us</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
            >
              Have questions or interested in our solutions? Reach out to our team and we'll get back
              to you shortly.
            </motion.p>
          </motion.div>

          {/* Background Elements */}
          <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-primary-orange/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-orange/3 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-10"
            >
              <div className="space-y-6">
                <h2 className="text-3xl font-bold">
                  Get in <span className="gradient-text">Touch</span>
                </h2>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Interested in learning more about our AI-powered transformation tools? We'd love to
                  hear from you. Fill out the form or use our contact information below.
                </p>
              </div>

              {/* Contact Details */}
              <div className="space-y-8">
                {/* Email */}
                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-start space-x-5 group"
                >
                  <div className="w-12 h-12 bg-primary-orange/10 border border-primary-orange/30 flex items-center justify-center group-hover:bg-primary-orange transition-all duration-300">
                    <SafeIcon
                      icon={FiMail}
                      className="w-6 h-6 text-primary-orange group-hover:text-black transition-colors"
                    />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Email</h3>
                    <a
                      href="mailto:info@quantumleverage.com"
                      className="text-gray-300 hover:text-primary-orange transition-colors"
                    >
                      info@quantumleverage.com
                    </a>
                  </div>
                </motion.div>

                {/* Location */}
                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-start space-x-5 group"
                >
                  <div className="w-12 h-12 bg-primary-orange/10 border border-primary-orange/30 flex items-center justify-center group-hover:bg-primary-orange transition-all duration-300">
                    <SafeIcon
                      icon={FiMapPin}
                      className="w-6 h-6 text-primary-orange group-hover:text-black transition-colors"
                    />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Location</h3>
                    <p className="text-gray-300">Fishers, IN</p>
                  </div>
                </motion.div>
              </div>

              {/* Map or Office Image */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                viewport={{ once: true }}
                className="relative aspect-video tech-border overflow-hidden"
              >
                <img
                  src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&h=450&fit=crop"
                  alt="Office Location"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40"></div>
                <div className="absolute bottom-4 left-4 px-4 py-2 bg-primary-orange text-black font-semibold">
                  Quantum Leverage HQ
                </div>
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="glass-effect p-8 lg:p-12"
            >
              <div className="space-y-8">
                <h2 className="text-3xl font-bold">Send a Message</h2>

                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="py-16 text-center space-y-6"
                  >
                    <div className="w-20 h-20 bg-green-500 rounded-full mx-auto flex items-center justify-center">
                      <SafeIcon icon={FiCheck} className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold">Message Sent!</h3>
                    <p className="text-gray-300">
                      Thank you for reaching out. We'll get back to you shortly.
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setIsSubmitted(false)}
                      className="bg-primary-orange text-black px-6 py-3 font-medium inline-block mt-4"
                    >
                      Send Another Message
                    </motion.button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Name */}
                      <div className="space-y-2">
                        <label htmlFor="name" className="text-gray-300 font-medium">
                          Your Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white focus:outline-none focus:border-primary-orange transition-colors"
                        />
                      </div>

                      {/* Email */}
                      <div className="space-y-2">
                        <label htmlFor="email" className="text-gray-300 font-medium">
                          Your Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white focus:outline-none focus:border-primary-orange transition-colors"
                        />
                      </div>
                    </div>

                    {/* Subject */}
                    <div className="space-y-2">
                      <label htmlFor="subject" className="text-gray-300 font-medium">
                        Subject
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white focus:outline-none focus:border-primary-orange transition-colors"
                      />
                    </div>

                    {/* Message */}
                    <div className="space-y-2">
                      <label htmlFor="message" className="text-gray-300 font-medium">
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="5"
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white focus:outline-none focus:border-primary-orange transition-colors"
                      ></textarea>
                    </div>

                    {/* Submit Button */}
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="bg-primary-orange text-black px-8 py-4 font-semibold text-lg hover:bg-primary-dark transition-all duration-200 flex items-center justify-center space-x-2 w-full"
                    >
                      <span>Send Message</span>
                      <SafeIcon icon={FiSend} className="w-5 h-5" />
                    </motion.button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-16"
          >
            <div className="text-center space-y-4">
              <h2 className="text-3xl font-bold">Frequently Asked Questions</h2>
              <div className="h-1 w-24 bg-primary-orange mx-auto"></div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  q: 'How can I schedule a demo?',
                  a: 'You can schedule a demo by filling out the contact form above or by emailing us directly at info@quantumleverage.com.',
                },
                {
                  q: 'Where are your offices located?',
                  a: 'Our headquarters are in Fishers, Indiana, with remote team members across the United States.',
                },
                {
                  q: 'Do you offer custom solutions?',
                  a: 'Yes, we offer tailored solutions to meet the specific needs of your organization.',
                },
                {
                  q: 'What industries do you serve?',
                  a: 'Our AI transformation tools are industry-agnostic and serve clients across technology, healthcare, finance, education, and manufacturing.',
                },
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                  className="glass-effect p-6 space-y-3"
                >
                  <h3 className="text-xl font-bold text-primary-orange">{faq.q}</h3>
                  <p className="text-gray-300">{faq.a}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;